import { Button } from "../../../shared/ui/button";
import { Title } from "../../../shared/ui/title";
import { Auth } from "../auth/auth";
import cx from "classnames";
import { ErrorMessage, Field, Form, Formik } from "formik";
import * as Yup from "yup";

import "./sign-in.scss";
import {
  useEventMutation,
  useLoginMutation,
  useLoginWithMsisdnMutation,
} from "../../data-access/store/services/auth.service";
import { useEffect, useState } from "react";
import { setMessage } from "../../../shared/data-access/store/message/messageSlice";
import { useAppDispatch, useAppSelector } from "../../../hooks/hooks";
import {
  selectInit,
  selectLanguage,
  selectTranslations,
} from "../../../shared/data-access/store/general/generalSlice";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { checkTranslationKey } from "../../../utils/translation";
import { selectIsAuthenticated } from "../../../auth/data-access/store/authSlice";
import Modal from "../../../shared/ui/modal/modal";
import { UpstreamServiceBanner } from "../../../shared/ui/upstream-service-banner";
import { NoAccountFound } from "../../../shared/ui/modal-notifications/no-account-found";
import { GenericError } from "../../../shared/ui/modal-notifications/generic-error";

const HYDRA_FALLBACK_FLAG = "hydra_fallback_msisdn_only";
const HYDRA_NO_ACCOUNT_FLAG = "hydra_no_account";

export function SignIn() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [login, { isLoading: loginIsLoading, error: loginError }] =
    useLoginMutation();
  const [
    loginWithMsisdn,
    { isLoading: loginWithMsisdnIsLoading, error: loginWithMsisdnError },
  ] = useLoginWithMsisdnMutation();
  const init = useAppSelector(selectInit);
  const lang = useAppSelector(selectLanguage);
  const translations = useAppSelector(selectTranslations);
  const [event] = useEventMutation();
  const location = useLocation();
  const loginForPurchase = location.state === "redirectedFromStore";
  const loginForUnsubscribe = location.state === "redirectedFromProfile";
  const baseLoginType = init?.users?.authType || "msisdn";
  const [isForcedMsisdnOnly, setIsForcedMsisdnOnly] = useState(false);
  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const [showNoAccountModal, setShowNoAccountModal] = useState(false);
  const [showGenericErrorModal, setShowGenericErrorModal] = useState(false);

  // Check for Hydra UUID fallback flag and force MSISDN-only login
  // Also show the subscribe modal if HE login failed with "no account".
  useEffect(() => {
    const hydraFallbackFlag = sessionStorage.getItem(HYDRA_FALLBACK_FLAG);
    if (hydraFallbackFlag) {
      setIsForcedMsisdnOnly(true);
    }

    const noAccountFlag = sessionStorage.getItem(HYDRA_NO_ACCOUNT_FLAG);
    if (noAccountFlag) {
      sessionStorage.removeItem(HYDRA_NO_ACCOUNT_FLAG);
      setShowNoAccountModal(true);
    }
  }, []);

  // Clear fallback flag when user successfully authenticates
  useEffect(() => {
    if (isAuthenticated && isForcedMsisdnOnly) {
      sessionStorage.removeItem(HYDRA_FALLBACK_FLAG);
      setIsForcedMsisdnOnly(false);
    }
  }, [isAuthenticated, isForcedMsisdnOnly]);

  // Determine effective login type (MSISDN-only if fallback flag is set)
  const loginType = isForcedMsisdnOnly ? "msisdn" : baseLoginType;

  useEffect(() => {
    if (loginError) {
      const errorMsg =
        (loginError as { data?: Error })?.data?.message ||
        checkTranslationKey(translations[lang]?.Error, "Error!");
      const status = (loginError as { status?: number })?.status;
      const lower = errorMsg.toLowerCase();
      const isWrongCredentials =
        status === 401 ||
        status === 403 ||
        lower.includes("invalid credentials") ||
        lower.includes("wrong credentials") ||
        lower.includes("wrong password") ||
        lower.includes("incorrect password") ||
        lower.includes("invalid password") ||
        lower.includes("unauthorized") ||
        lower.includes("invalid_grant");

      if (isWrongCredentials) {
        const invalidCredentialsErrorMsg = checkTranslationKey(
          translations[lang]?.Invalid_Credentials,
          "Your Username or Password is wrong."
        );
        dispatch(
          setMessage({ content: invalidCredentialsErrorMsg, type: "error" })
        );
      } else {
        setShowGenericErrorModal(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loginError]);

  useEffect(() => {
    if (loginWithMsisdnError) {
      const errorMsg =
        (loginWithMsisdnError as { data?: Error })?.data?.message ||
        checkTranslationKey(translations[lang]?.Error, "Error!");
      if (errorMsg) {
        // Check if error is about no account found
        const isNoAccountError =
          errorMsg.toLowerCase().includes("not found") ||
          errorMsg.toLowerCase().includes("no account") ||
          errorMsg.toLowerCase().includes("account does not exist") ||
          errorMsg.toLowerCase().includes("could not login");

        if (isNoAccountError) {
          // Show the no account modal instead of error message
          setShowNoAccountModal(true);
        } else {
          // Show other errors as messages
          dispatch(setMessage({ content: errorMsg, type: "error" }));
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loginWithMsisdnError]);

  const prefix = init?.users?.msisdn?.prefix || "";
  const phoneMaxLength = init?.users?.msisdn?.maxLength || 9;
  const phoneMinLength = init?.users?.msisdn?.minLength || 9;
  const pinMaxLength = init?.users?.pin?.maxLength || 6;
  const pinMinLength = init?.users?.pin?.minLength || 4;

  const SignInSchema = Yup.object().shape({
    phone: Yup.string()
      .matches(
        /^[0-9]+$/,
        checkTranslationKey(
          translations[lang]?.The_phone_number_must_be_only_digits,
          "The phone number must be only digits"
        )
      )
      .min(
        phoneMinLength,
        checkTranslationKey(
          translations[lang]?.The_phone_number_is_too_short,
          "The phone number is too short."
        )
      )
      .max(
        phoneMaxLength,
        checkTranslationKey(
          translations[lang]?.The_phone_number_is_too_long,
          "The phone number is too long."
        )
      )
      .required(
        checkTranslationKey(
          translations[lang]?.The_phone_number_is_required,
          "The phone number is required."
        )
      ),
    ...(loginType === "password"
      ? {
          password: Yup.string()
            .matches(
              /^[0-9]+$/,
              checkTranslationKey(
                translations[lang]?.The_password_must_be_only_digits,
                "The password must be only digits"
              )
            )
            .min(
              pinMinLength,
              checkTranslationKey(
                translations[lang]?.The_password_is_too_short,
                "The password is too short."
              )
            )
            .max(
              pinMaxLength,
              checkTranslationKey(
                translations[lang]?.The_password_is_too_long,
                "The password is too long."
              )
            )
            .required(
              checkTranslationKey(
                translations[lang]?.The_password_is_required,
                "The password is required."
              )
            ),
        }
      : {}),
  });

  return (
    <div className="auth-page">
      <Auth
        className="auth-shell-content"
        header={
          <Title>
            {checkTranslationKey(translations[lang]?.Sign_In, "Sign In")}
          </Title>
        }
        hasFooter
      >
        <div className="form-title form-title-login">
          {checkTranslationKey(
            translations[lang]?.Enter_your_BattleUp_account,
            "Enter your BattleUp account"
          )}
        </div>
        <div className="notice">
          <span>
            {checkTranslationKey(
              translations[lang]?.Telephone_number_for_your_account,
              "Telephone number for your account"
            )}
          </span>
        </div>
        {loginForPurchase && (
          <div className="notice">
            <span>
              {checkTranslationKey(
                translations[lang]
                  ?.Please_enter_your_credentials_to_proceed_with_the_purchase,
                "Please enter your credentials to proceed with the purchase"
              )}
            </span>
          </div>
        )}
        {loginForUnsubscribe && (
          <div className="notice">
            <span>
              {checkTranslationKey(
                translations[lang]
                  ?.Please_enter_your_credentials_to_proceed_with_the_unsubscribe,
                "Please enter your credentials to proceed with the unsubscribe"
              )}
            </span>
          </div>
        )}
        <div className="form-container">
          <Formik
            initialValues={
              loginType === "password"
                ? { phone: "", password: "" }
                : { phone: "" }
            }
            validationSchema={SignInSchema}
            onSubmit={(values) => {
              if (loginType === "password" && values.password) {
                login({
                  msisdn: prefix + values.phone,
                  password: values.password,
                });
              } else if (loginType === "msisdn") {
                loginWithMsisdn({
                  msisdn: prefix + values.phone,
                });
              }
              event({ type: "LOGIN" });
              localStorage.setItem("fromLogin", "true");
            }}
          >
            {({ errors, touched }) => (
              <>
                {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
                <Form {...({} as any)}>
                  <div
                    className={cx("form-input-row", {
                      danger: errors.phone && touched.phone,
                      success: !errors.phone,
                    })}
                  >
                    <div className="prefix-input">
                      <span className="prefix">+{prefix}</span>
                      <Field
                        type="tel"
                        name="phone"
                        className="half-input"
                        placeholder={checkTranslationKey(
                          translations[lang]?.Your_phone_number,
                          "Your phone number"
                        )}
                        autoComplete="off"
                        maxLength={phoneMaxLength}
                        required
                      />
                    </div>
                    <ErrorMessage name="phone">
                      {(msg) => (
                        <div className="error-msg">
                          <img
                            src="/img/icns/alert-fill.svg"
                            alt="alert"
                            className="color-svg"
                          />
                          {msg}
                        </div>
                      )}
                    </ErrorMessage>
                  </div>

                  {loginType === "password" && (
                    <div
                      className={cx("form-input-row", {
                        danger: errors.password && touched.password,
                      })}
                    >
                      <div className="notice">
                        <span>
                          {checkTranslationKey(
                            translations[lang]?.Password,
                            "Password"
                          )}
                        </span>
                      </div>
                      <Field
                        autoComplete="off"
                        className="full-input"
                        type="password"
                        name="password"
                        placeholder={checkTranslationKey(
                          translations[lang]?.Your_password,
                          "Your password"
                        )}
                        maxLength={pinMaxLength}
                      />
                      <ErrorMessage name="password">
                        {(msg) => (
                          <div className="error-msg">
                            <img
                              src="/img/icns/alert-fill.svg"
                              alt="alert"
                              className="color-svg"
                            />
                            {msg}
                          </div>
                        )}
                      </ErrorMessage>
                    </div>
                  )}

                  <div>
                    <div className="submit">
                      {/* @ts-ignore */}
                      <Button
                        buttonType="success"
                        type="submit"
                        value="Submit"
                        disabled={loginIsLoading || loginWithMsisdnIsLoading}
                      >
                        {loginIsLoading
                          ? checkTranslationKey(
                              translations[lang]?.Signing_in,
                              "Signing in..."
                            )
                          : checkTranslationKey(
                              translations[lang]?.Sign_in,
                              "Sign in"
                            )}
                      </Button>
                    </div>
                    <div className="forgot-area">
                      {/*<p*/}
                      {/*  style={{*/}
                      {/*    paddingTop: "8px",*/}
                      {/*    paddingBottom: "8px",*/}
                      {/*    fontWeight: "bold",*/}
                      {/*    fontSize: "14px",*/}
                      {/*  }}*/}
                      {/*>*/}
                      {/*  {checkTranslationKey(*/}
                      {/*    translations[lang]?.Signing_in_cost,*/}
                      {/*    "Cost: 1.50 GHS/Day"*/}
                      {/*  )}*/}
                      {/*</p>*/}
                      <Link
                        to="/auth/forgot-password"
                        className="forgot-password"
                      >
                        {checkTranslationKey(
                          translations[lang]?.Forgot_your_password,
                          "Forgot your password?"
                        )}
                      </Link>
                    </div>
                  </div>
                </Form>
              </>
            )}
          </Formik>
        </div>
      </Auth>

      {!loginForPurchase
        ? !loginForUnsubscribe && (
            <div
              className="no-account-area content"
              style={{ marginTop: "25px" }}
            >
              <div className="no-account">
                {checkTranslationKey(
                  translations[lang]?.Don_t_have_an_account,
                  "Don't have an account?"
                )}
              </div>
              <div className="button-submit">
                <Button
                  onClick={() => navigate("/auth/sign-up")}
                  buttonType="white"
                  small
                >
                  {checkTranslationKey(translations[lang]?.Sign_up, "Sign up")}
                </Button>
              </div>
            </div>
          )
        : null}

      <div className="stream-service">
        <UpstreamServiceBanner />
      </div>

      <Modal
        show={showNoAccountModal}
        setShow={setShowNoAccountModal}
        hideCloseButton={true}
      >
        <NoAccountFound
          onButtonClick={() => {
            setShowNoAccountModal(false);
          }}
        />
      </Modal>

      <Modal
        show={showGenericErrorModal}
        setShow={setShowGenericErrorModal}
        hideCloseButton={true}
      >
        <GenericError
          onButtonClick={() => {
            setShowGenericErrorModal(false);
            navigate("/");
          }}
        />
      </Modal>
    </div>
  );
}

export default SignIn;
